import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import type { ContactAttachment } from "@/types/vacancy"
import { ExternalLink, FileText, Paperclip } from "lucide-react"
import { useMemo, useState } from "react"

type CandidateAttachmentsProps = {
  attachments?: ContactAttachment[] | null
  loading?: boolean
  error?: string | null
  onRetry?: () => void
  className?: string
}

const dateFormatter = new Intl.DateTimeFormat("nl-NL", {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
})

const formatCreatedAt = (input?: string | null) => {
  if (!input) {
    return null
  }
  const parsed = new Date(input)
  if (Number.isNaN(parsed.getTime())) {
    return null
  }
  return dateFormatter.format(parsed)
}

const resolveAttachmentLabel = (attachment: ContactAttachment, index: number) => {
  const explicitName = attachment.name?.trim()
  if (explicitName) {
    return explicitName
  }
  if (attachment.source === "cv") {
    return "CV"
  }
  return `Bijlage ${index + 1}`
}

const CandidateAttachments = ({
  attachments = [],
  loading = false,
  error = null,
  onRetry,
  className,
}: CandidateAttachmentsProps) => {
  const attachmentList = attachments ?? []
  const [showAll, setShowAll] = useState(false)
  const hasOverflow = attachmentList.length > 5
  const visibleAttachments = useMemo(
    () => (showAll ? attachmentList : attachmentList.slice(0, 5)),
    [attachmentList, showAll]
  )

  return (
    <Card className={cn("rounded-xl border-slate-200 shadow-none", className)}>
      <CardHeader>
        <CardTitle className="text-xl font-semibold tracking-wide text-black">Bijlagen</CardTitle>
        <CardDescription>
          Bestanden gekoppeld aan dit contact in HubSpot.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="space-y-2" role="status" aria-live="polite">
            <div className="h-12 w-full rounded-md skeleton-shimmer" aria-hidden="true" />
            <div className="h-12 w-full rounded-md skeleton-shimmer" aria-hidden="true" />
            <span className="sr-only">Bijlagen worden geladen</span>
          </div>
        ) : null}

        {!loading && error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            <p>Kon bijlagen niet laden.</p>
            {onRetry ? (
              <button
                type="button"
                className="mt-2 text-red-700 underline underline-offset-2"
                onClick={onRetry}
              >
                Opnieuw proberen
              </button>
            ) : null}
          </div>
        ) : null}

        {!loading && !error && attachmentList.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-200 px-4 py-6 text-sm text-slate-500">
            Geen bijlagen gevonden voor dit contact.
          </div>
        ) : null}

        {!loading && !error && attachmentList.length > 0 ? (
          <div className="relative">
            <ul className="space-y-2">
              {visibleAttachments.map((attachment, index) => {
                const absoluteIndex = showAll ? index : index
                const label = resolveAttachmentLabel(attachment, absoluteIndex)
                const createdAt = formatCreatedAt(attachment.createdAt)
                return (
                  <li key={`${attachment.id}-${index}`}>
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-4 py-3 transition-colors hover:bg-slate-50"
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        {attachment.source === "cv" ? (
                          <FileText className="size-4 shrink-0 text-slate-600" />
                        ) : (
                          <Paperclip className="size-4 shrink-0 text-slate-600" />
                        )}
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium text-slate-900">
                            {label}
                          </span>
                          <span className="block text-xs text-slate-500">
                            {createdAt ? `Toegevoegd op ${createdAt}` : "Geen datum beschikbaar"}
                          </span>
                        </span>
                      </span>
                      <ExternalLink className="size-4 shrink-0 text-slate-500" />
                    </a>
                  </li>
                )
              })}
            </ul>
            {!showAll && hasOverflow ? (
              <div
                className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white via-white/85 to-transparent"
                aria-hidden="true"
              />
            ) : null}
          </div>
        ) : null}

        {!loading && !error && hasOverflow ? (
          <button
            type="button"
            className="text-sm font-medium text-primary underline-offset-2 hover:underline"
            onClick={() => setShowAll((current) => !current)}
          >
            {showAll ? "Toon minder" : `Toon alle bijlagen (${attachmentList.length})`}
          </button>
        ) : null}
      </CardContent>
    </Card>
  )
}

export default CandidateAttachments
